import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import i18n from '../i18n';
import { auth, db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const profileDoc = await getDoc(doc(db, COLLECTIONS.USERS, firebaseUser.uid));
          if (profileDoc.exists()) {
            const data = profileDoc.data();
            if (data.status === 'disabled') {
              await signOut(auth);
              setUser(null);
              setProfile(null);
            } else {
              setUser(firebaseUser);
              setProfile(data);
              if (data.language && i18n.language !== data.language) {
                i18n.changeLanguage(data.language);
              }
              // Update last login
              updateDoc(doc(db, COLLECTIONS.USERS, firebaseUser.uid), {
                lastLoginAt: serverTimestamp(),
              }).catch(() => {});
            }
          } else {
            setUser(firebaseUser);
            setProfile(null);
          }
        } else {
          setUser(null);
          setProfile(null);
        }
      } catch {
        setUser(firebaseUser || null);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const isAdmin = profile?.role === 'admin';
  const isProduction = profile?.role === 'production';
  const companyId = profile?.companyId;

  return (
    <AuthContext.Provider value={{ user, profile, loading, login, logout, isAdmin, isProduction, companyId }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
